/**
 * Global offline sync state + auto-flush on reconnect.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useConnectivity } from '../contexts/ConnectivityContext.jsx';
import { useAuth } from './AuthContext.jsx';
import {
  countPendingOutbox,
  countErrorOutbox,
  listRefusedOutbox,
  dismissRefusedOutbox,
  recoverStuckSyncing,
  resetErroredToPending,
} from '../offline/outbox.service.js';
import { flushSyncQueue, subscribeSyncEngine } from '../offline/syncEngine.js';
import { OFFLINE_INTERACTION_ENABLED, SYNC_INTERVAL_MS } from '../offline/offlineConfig.js';

const OfflineSyncContext = createContext(null);

export function OfflineSyncProvider({ children }) {
  const { isReachable } = useConnectivity();
  // F-29 / F-35 : la file ne se rejoue qu'avec une session, et seulement pour
  // l'utilisateur qui l'a remplie (jamais sous le compte d'un autre).
  const { isAuthenticated, user } = useAuth();
  const ownerId = isAuthenticated && user?.id != null ? String(user.id) : null;
  const [pendingCount, setPendingCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  // Refus définitifs du serveur (F-07) : une ligne par action, avec son motif.
  const [refusedActions, setRefusedActions] = useState([]);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncError, setLastSyncError] = useState(null);

  const refreshPendingCount = useCallback(async () => {
    if (!OFFLINE_INTERACTION_ENABLED || !ownerId) {
      setPendingCount(0);
      setErrorCount(0);
      setRefusedActions([]);
      return 0;
    }
    const [count, errors, refused] = await Promise.all([
      countPendingOutbox(ownerId),
      countErrorOutbox(ownerId),
      listRefusedOutbox(ownerId),
    ]);
    setPendingCount(count);
    setErrorCount(errors);
    setRefusedActions(refused);
    return count;
  }, [ownerId]);

  const runSync = useCallback(async () => {
    if (!OFFLINE_INTERACTION_ENABLED || !isReachable || !ownerId) return { flushed: 0, failed: 0 };
    setSyncing(true);
    setLastSyncError(null);
    try {
      const result = await flushSyncQueue({ userId: ownerId });
      await refreshPendingCount();
      if (!result.skipped && result.flushed > 0) {
        setLastSyncAt(new Date().toISOString());
      }
      if (result.failed > 0) {
        setLastSyncError(`${result.failed} action(s) n'ont pas pu être synchronisées.`);
      }
      return result;
    } catch (err) {
      setLastSyncError(err?.message || 'Erreur de synchronisation');
      throw err;
    } finally {
      setSyncing(false);
    }
  }, [isReachable, ownerId, refreshPendingCount]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // One-time recovery of actions orphaned in SYNCING by a prior session's
      // interrupted flush, so the count is accurate even before the first sync.
      if (OFFLINE_INTERACTION_ENABLED) {
        await recoverStuckSyncing().catch(() => {});
      }
      if (!cancelled) await refreshPendingCount();
    })();
    const unsub = subscribeSyncEngine(() => {
      refreshPendingCount();
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [refreshPendingCount]);

  useEffect(() => {
    if (!OFFLINE_INTERACTION_ENABLED || !ownerId) return undefined;
    if (isReachable) {
      runSync().catch(() => {});
    }
    return undefined;
  }, [isReachable, ownerId, runSync]);

  useEffect(() => {
    if (!OFFLINE_INTERACTION_ENABLED || !isReachable || !ownerId) return undefined;
    const id = window.setInterval(() => {
      runSync().catch(() => {});
    }, SYNC_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isReachable, ownerId, runSync]);

  const retryErrored = useCallback(async () => {
    if (!OFFLINE_INTERACTION_ENABLED || !ownerId) return { flushed: 0, failed: 0 };
    await resetErroredToPending(ownerId);
    await refreshPendingCount();
    return runSync();
  }, [ownerId, refreshPendingCount, runSync]);

  const dismissRefused = useCallback(async (id) => {
    await dismissRefusedOutbox(id);
    await refreshPendingCount();
  }, [refreshPendingCount]);

  const value = {
    pendingCount,
    errorCount,
    refusedActions,
    dismissRefused,
    lastSyncAt,
    syncing,
    lastSyncError,
    refreshPendingCount,
    runSync,
    retryErrored,
  };

  return (
    <OfflineSyncContext.Provider value={value}>{children}</OfflineSyncContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOfflineSync() {
  const ctx = useContext(OfflineSyncContext);
  if (!ctx) {
    throw new Error('useOfflineSync must be used within OfflineSyncProvider');
  }
  return ctx;
}
